import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { motion, AnimatePresence } from 'framer-motion';
import { trackCoreFeatureUsed } from '../analytics/mixpanel';
import { ZoomIn, ZoomOut, RotateCcw, Search, X, Users, Briefcase, MapPin, ExternalLink, ChevronRight, Maximize2, Minimize2, Lock } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

/* ── Tier definitions ── */
const WORK_TIERS = [
  { key: 'T1', label: 'Same company · Similar role · Same tenure', color: '#10B981' },
  { key: 'T2', label: 'Same company · Similar role · ±2 yrs', color: '#34D399' },
  { key: 'T3', label: 'Same company · Same tenure', color: '#059669' },
  { key: 'T4', label: 'Same company · ±2 yrs', color: '#047857' },
  { key: 'T5', label: 'Same company · Similar role', color: '#065F46' },
  { key: 'T6', label: 'Same company background', color: '#064E3B' },
];
const EDU_TIERS = [
  { key: 'T1', label: 'Same college · Same branch · Same batch', color: '#8B5CF6' },
  { key: 'T2', label: 'Same college · Same branch · ±2 yrs', color: '#A78BFA' },
  { key: 'T3', label: 'Same college · Same batch · Diff branch', color: '#7C3AED' },
  { key: 'T4', label: 'Same college · ±2 yrs · Diff branch', color: '#6D28D9' },
  { key: 'T5', label: 'Same college · Same branch', color: '#5B21B6' },
  { key: 'T6', label: 'Same college background', color: '#4C1D95' },
];
const getWorkTierHelp = (companyName = 'this company') => ({
  T1: `People who worked at ${companyName}, in the same role, with the same tenure.`,
  T2: `People who worked at ${companyName}, in the same role, with tenure within ±2 years.`,
  T3: `People who worked at ${companyName}, with the same tenure - role may differ.`,
  T4: `People who worked at ${companyName}, with tenure within ±2 years - role may differ.`,
  T5: `People who worked at ${companyName}, in the same role - tenure may differ.`,
  T6: `People who worked at ${companyName} - role and tenure may differ.`,
});

const getEduTierHelp = (collegeName = 'this college') => ({
  T1: `Your Friends from ${collegeName}, now working inside this companies.`,
  T2: `Your Department Seniors(±2 yrs) from ${collegeName}, now working inside this companies.`,
  T3: `Your Batchmates from ${collegeName}, now working inside this companies.`,
  T4: `Your close Seniors/juniors (±2 yrs) from ${collegeName}, now working inside this companies.`,
  T5: `Your Department Seniors from ${collegeName}, now working inside this companies.`,
  T6: `${collegeName} almni,now working inside this companies.`,
});

// const getWorkTierHelp = (companyName = 'this company') => ({
//   T1: `People who worked at ${companyName}, in the same role, with the same tenure.`,
//   T2: `People who worked at ${companyName}, in the same role, with tenure within ±2 years.`,
//   T3: `People who worked at ${companyName}, with the same tenure - role may differ.`,
//   T4: `People who worked at ${companyName}, with tenure within ±2 years - role may differ.`,
//   T5: `People who worked at ${companyName}, in the same role - tenure may differ.`,
//   T6: `People who worked at ${companyName} - role and tenure may differ.`,
// });

// const getEduTierHelp = (collegeName = 'this college') => ({
//   T1: `People who studied at ${collegeName}, in the same branch, from the same batch.`,
//   T2: `People who studied at ${collegeName}, in the same branch, with batch within ±2 years.`,
//   T3: `People who studied at ${collegeName}, in the same batch - branch may differ.`,
//   T4: `People who studied at ${collegeName}, with batch within ±2 years - branch may differ.`,
//   T5: `People who studied at ${collegeName}, in the same branch - batch may differ.`,
//   T6: `People who studied at ${collegeName} - branch and batch may differ.`,
// });

function getTagLabels(c) {
  const tags = Array.isArray(c.relation_tags) ? c.relation_tags : [];
  if (tags.length > 0) return tags.map(t => typeof t === 'string' ? t : (t?.label || '')).filter(Boolean);
  const raw = String(c.strongest_relation || '').trim();
  if (raw) return raw.split('·').map(x => x.trim()).filter(Boolean);
  return [];
}

function getTooltipTagLabels(c, tagTierContext = 'default') {
  if (tagTierContext === 'cross_network') {
    const entities = Array.isArray(c?.strongest_entities)
      ? c.strongest_entities.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 2)
      : [];
    if (entities.length > 0) return entities;
    const rawTags = Array.isArray(c?.relation_tags) ? c.relation_tags : [];
    const vals = rawTags
      .map((t) => typeof t === 'string' ? t : (t?.label || ''))
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .map((label) => {
        if (/^work\s*:/i.test(label)) return label.replace(/^work\s*:/i, '').trim();
        if (/^college\s*:/i.test(label)) return label.replace(/^college\s*:/i, '').trim();
        return '';
      })
      .filter(Boolean);
    return [...new Set(vals)].slice(0, 2);
  }

  const rawTags = Array.isArray(c?.relation_tags) ? c.relation_tags : [];
  const labels = rawTags
    .map((t) => typeof t === 'string' ? t : (t?.label || ''))
    .map((t) => String(t || '').trim())
    .filter(Boolean);
  const out = [];
  const seen = new Set();
  labels.forEach((label) => {
    if (
      /^best person background$/i.test(label)
      || /^work details$/i.test(label)
      || /^college details$/i.test(label)
      || /^roles$/i.test(label)
      || /^tenure$/i.test(label)
      || /^branches$/i.test(label)
      || /^batches$/i.test(label)
    ) return;
    let next = label;
    if (/^work\s*:/i.test(next)) {
      next = next.replace(/^work\s*:/i, '').trim();
    } else if (/^college\s*:/i.test(next)) {
      next = next.replace(/^college\s*:/i, '').trim();
    }
    if (!next || seen.has(next)) return;
    seen.add(next);
    out.push(next);
  });
  return out;
}

function getVisibleTierLabels(c, useTagTiers, tagTierMode = 'single', tagTierContext = 'default') {
  if (!useTagTiers) return getTagLabels(c);

  const genericToken = (s) => /^(best person background|work details|college details|roles|tenure|branches|batches)$/i.test(String(s || '').trim());

  if (tagTierContext === 'cross_network') {
    const entities = Array.isArray(c?.strongest_entities)
      ? c.strongest_entities.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 2)
      : [];
    if (entities.length >= 2 && tagTierMode === 'combo') return [`${entities[0]} + ${entities[1]}`];
    if (entities.length > 0) return entities;

    const rawTags = Array.isArray(c?.relation_tags) ? c.relation_tags : [];
    const vals = rawTags
      .map((t) => typeof t === 'string' ? t : (t?.label || ''))
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .map((label) => {
        if (/^work\s*:/i.test(label)) return label.replace(/^work\s*:/i, '').trim();
        if (/^college\s*:/i.test(label)) return label.replace(/^college\s*:/i, '').trim();
        return '';
      })
      .filter(Boolean);
    const uniq = [...new Set(vals)].slice(0, 2);
    if (uniq.length >= 2 && tagTierMode === 'combo') return [`${uniq[0]} + ${uniq[1]}`];
    if (uniq.length > 0) return uniq;
    return [];
  }

  if (tagTierContext === 'best_work' || tagTierContext === 'best_education') {
    const strongestEntities = Array.isArray(c?.strongest_entities)
      ? c.strongest_entities
        .map((x) => String(x || '').trim())
        .filter((x) => x && !genericToken(x))
      : [];
    if (strongestEntities.length > 0) {
      if (tagTierMode === 'combo' && strongestEntities.length >= 2) {
        return [`${strongestEntities[0]} + ${strongestEntities[1]}`];
      }
      return strongestEntities.slice(0, 3);
    }

    const rawTags = Array.isArray(c?.relation_tags) ? c.relation_tags : [];
    const vals = rawTags
      .map((t) => typeof t === 'string' ? t : (t?.label || ''))
      .map((s) => String(s || '').trim())
      .filter((s) => s && !genericToken(s))
      .map((label) => {
        if (/^work\s*:/i.test(label)) return label.replace(/^work\s*:/i, '').trim();
        if (/^college\s*:/i.test(label)) return label.replace(/^college\s*:/i, '').trim();
        return label;
      })
      .filter((s) => s && !genericToken(s));
    const uniq = [...new Set(vals)];
    if (tagTierMode === 'combo' && uniq.length >= 2) return [`${uniq[0]} + ${uniq[1]}`];
    if (uniq.length > 0) return uniq.slice(0, 3);
    return [];
  }

  const strongestEntities = Array.isArray(c?.strongest_entities)
    ? c.strongest_entities.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  if (strongestEntities.length > 0) {
    if (tagTierMode === 'combo') {
      return [strongestEntities.join(' + ')];
    }
    return strongestEntities.slice(0, 3);
  }
  const tagLabels = getTagLabels(c).map((x) => String(x || '').trim()).filter(Boolean);
  if (tagTierMode === 'combo' && tagLabels.length > 0) {
    return [tagLabels.join(' + ')];
  }
  if (tagLabels.length > 0) return tagLabels.slice(0, 3);
  return [];
}

function classifyTier(labels, type) {
  if (type === 'work') {
    const role = labels.some(l => /same role|similar role/i.test(l));
    const sameTen = labels.some(l => /same tenure/i.test(l));
    const nearTen = labels.some(l => /near tenure/i.test(l));
    if (role && sameTen) return 'T1';
    if (role && nearTen) return 'T2';
    if (sameTen) return 'T3';
    if (nearTen) return 'T4';
    if (role) return 'T5';
    return 'T6';
  }
  const branch = labels.some(l => /same branch|similar branch/i.test(l));
  const sameBatch = labels.some(l => /same batch/i.test(l));
  const nearBatch = labels.some(l => /near batch/i.test(l));
  if (branch && sameBatch) return 'T1';
  if (branch && nearBatch) return 'T2';
  if (sameBatch) return 'T3';
  if (nearBatch) return 'T4';
  if (branch) return 'T5';
  return 'T6';
}

function buildTagTierGraph(companies, centerLabel, centerType, expandedTiers, tagTierMode = 'single', tagTierContext = 'default') {
  const nodes = [];
  const links = [];
  const colors = centerType === 'work'
    ? ['#3B82F6', '#2563EB', '#60A5FA', '#1D4ED8', '#93C5FD', '#1E40AF']
    : ['#A78BFA', '#8B5CF6', '#C4B5FD', '#7C3AED', '#DDD6FE', '#6D28D9'];
  const groups = new Map();
  (companies || []).forEach((c) => {
    const labels = getVisibleTierLabels(c, true, tagTierMode, tagTierContext);
    labels.forEach((label) => {
      const key = String(label || '').trim();
      if (!key) return;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    });
  });

  const tiers = Array.from(groups.entries())
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
    .map(([label], idx) => ({
      key: `TAG_${idx + 1}`,
      label,
      color: colors[idx % colors.length],
    }));

  const tierHelp = {};
  const tierGroups = {};

  nodes.push({ id: '__center__', kind: 'center', label: centerLabel || 'Company', color: centerType === 'work' ? '#34D399' : '#8B5CF6', size: 22 });

  tiers.forEach((t) => {
    const members = groups.get(t.label) || [];
    const count = members.length;
    if (count === 0) return;
    const tierId = `tier_${t.key}`;
    tierGroups[t.key] = count;
    const parts = String(t.label || '').split('+').map((s) => s.trim()).filter(Boolean);
    const first = parts[0] || 'this background';
    const second = parts[1] || 'this background';
    if (tagTierContext === 'cross_network') {
      tierHelp[t.key] = `People who worked at ${first} and studied at ${second}, now in these companies.`;
    } else if (tagTierContext === 'best_work') {
      tierHelp[t.key] = `People who worked at both ${first} and ${second}, now in these companies.`;
    } else if (tagTierContext === 'best_education') {
      tierHelp[t.key] = `People who studied at ${first} and ${second}, now working in these companies.`;
    } else {
      tierHelp[t.key] = `Companies grouped under "${t.label}" for ${centerLabel || 'the selected anchor'}.`;
    }
    nodes.push({ id: tierId, kind: 'tier', tierKey: t.key, label: t.label, color: t.color, size: 13, companyCount: count, isExpanded: !!expandedTiers[t.key] });
    links.push({ source: '__center__', target: tierId, kind: 'spine', color: t.color });

    if (expandedTiers[t.key]) {
      const added = new Set();
      members.forEach((c, i) => {
        const compId = `comp_${c.organization_id || i}_${t.key}`;
        if (added.has(compId)) return;
        added.add(compId);
        nodes.push({ id: compId, kind: 'company', label: c.organization || 'Unknown', color: t.color, size: Math.min(8, 3 + Math.sqrt(c.count || 1) * 1.5), data: c });
        links.push({ source: tierId, target: compId, kind: 'branch', color: t.color + '60' });
      });
    }
  });

  return { nodes, links, tiers, tierHelp, tierGroups };
}

function buildGraph(companies, centerLabel, centerType, expandedTiers) {
  const nodes = [];
  const links = [];
  const tiers = centerType === 'work' ? WORK_TIERS : EDU_TIERS;
  const groups = {};
  tiers.forEach(t => { groups[t.key] = []; });

  (companies || []).forEach(c => {
    const tierKey = classifyTier(getTagLabels(c), centerType);
    if (groups[tierKey]) groups[tierKey].push(c);
  });

  nodes.push({ id: '__center__', kind: 'center', label: centerLabel || 'Company', color: centerType === 'work' ? '#34D399' : '#8B5CF6', size: 22 });

  tiers.forEach(t => {
    const count = groups[t.key].length;
    if (count === 0) return;
    const tierId = `tier_${t.key}`;
    nodes.push({ id: tierId, kind: 'tier', tierKey: t.key, label: t.label, color: t.color, size: 13, companyCount: count, isExpanded: !!expandedTiers[t.key] });
    links.push({ source: '__center__', target: tierId, kind: 'spine', color: t.color });

    if (expandedTiers[t.key]) {
      const added = new Set();
      groups[t.key].forEach((c, i) => {
        const compId = `comp_${c.organization_id || i}_${t.key}`;
        if (added.has(compId)) return;
        added.add(compId);
        nodes.push({ id: compId, kind: 'company', label: c.organization || 'Unknown', color: t.color, size: Math.min(8, 3 + Math.sqrt(c.count || 1) * 1.5), data: c });
        links.push({ source: tierId, target: compId, kind: 'branch', color: t.color + '60' });
      });
    }
  });
  const tierHelp = centerType === 'work' ? getWorkTierHelp(centerLabel || 'this company') : getEduTierHelp(centerLabel || 'this college');
  const tierGroups = {};
  tiers.forEach((t) => { tierGroups[t.key] = groups[t.key]?.length || 0; });
  return { nodes, links, tiers, tierHelp, tierGroups };
}

/* ── Tooltip ── */
const NodeTooltip = ({ node, isDark, position, onFindPeople, onFindRoles, onMouseEnter, onMouseLeave, tagTierContext = 'default' }) => {
  if (!node || node.kind !== 'company' || !node.data) return null;
  const c = node.data;
  const isLockedRow = Boolean(c?.__locked);
  const bg = isDark ? 'rgba(17,24,39,0.97)' : 'rgba(255,255,255,0.98)';
  const border = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  const txtMain = isDark ? '#fff' : '#0f172a';
  const txtMuted = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const accent = isDark ? '#F59E0B' : '#2563eb';
  const tags = getTooltipTagLabels(c, tagTierContext);

  return (
    <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.92 }} transition={{ duration: 0.15 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ position: 'absolute', left: position.x + 16, top: position.y - 10, zIndex: 100, width: 280, padding: '16px 18px', borderRadius: 16, background: bg, border: `1px solid ${border}`, boxShadow: isDark ? '0 20px 50px rgba(0,0,0,0.6)' : '0 16px 40px rgba(0,0,0,0.12)', pointerEvents: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${node.color}20`, border: `1.5px solid ${node.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: node.color }}>{(c.organization || '?')[0]}</div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: txtMain, margin: 0 }}>{c.organization}</p>
          <p style={{ fontSize: 11, color: txtMuted, margin: 0 }}>{c.count || 0} contacts</p>
        </div>
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {tags.slice(0, 3).map((tag, i) => (
            <span key={i} style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', color: isDark ? 'rgba(255,255,255,0.6)' : '#475569' }}>{typeof tag === 'string' ? tag : tag?.label}</span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
        <button onClick={e => { e.stopPropagation(); onFindPeople?.(c); }} style={{ flex: 1, padding: '8px 0', borderRadius: 10, fontSize: 11, fontWeight: 700, background: accent, color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          {isLockedRow ? <><Lock size={12} />Find my contact</> : <><Users size={12} /> Find my contact <ChevronRight size={11} /></>}
        </button>
        <button onClick={e => { e.stopPropagation(); onFindRoles?.(c); }} style={{ flex: 1, padding: '8px 0', borderRadius: 10, fontSize: 11, fontWeight: 700, background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0', color: isDark ? 'rgba(255,255,255,0.8)' : '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          {isLockedRow ? <><Lock size={12} />Find open roles</> : <><Briefcase size={12} /> Find open roles <ExternalLink size={10} /></>}
        </button>
      </div>
    </motion.div>
  );
};

const TierTooltip = ({ node, isDark, position, description }) => {
  if (!node || node.kind !== 'tier' || !description) return null;
  const bg = isDark ? 'rgba(17,24,39,0.97)' : 'rgba(255,255,255,0.98)';
  const border = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  const txtMain = isDark ? '#fff' : '#0f172a';
  const txtMuted = isDark ? 'rgba(255,255,255,0.62)' : '#475569';
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration: 0.14 }}
      style={{
        position: 'absolute',
        left: position.x + 16,
        top: position.y - 10,
        zIndex: 100,
        width: 320,
        padding: '12px 14px',
        borderRadius: 12,
        background: bg,
        border: `1px solid ${border}`,
        boxShadow: isDark ? '0 16px 38px rgba(0,0,0,0.5)' : '0 12px 28px rgba(0,0,0,0.12)',
        pointerEvents: 'none',
      }}
    >
      <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: txtMain }}>{node.label}</p>
      <p style={{ margin: '6px 0 0', fontSize: 11, lineHeight: 1.5, color: txtMuted }}>{description}</p>
    </motion.div>
  );
};

/* ── Main Component ── */
const NetworkMapView = ({ centerLabel, centerType = 'work', companies = [], orgId, companyName, onCompanyClick, style = {}, useTagTiers = false, tagTierMode = 'single', tagTierContext = 'default' }) => {
  const { isDark } = useTheme();
  const fgRef = useRef();
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 800, h: 520 });
  const [hoverNode, setHoverNode] = useState(null);
  const [activeNode, setActiveNode] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [search, setSearch] = useState('');
  const [expandedTiers, setExpandedTiers] = useState({});
  const [isFullView, setIsFullView] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);

  const graph = useMemo(() => (
    useTagTiers
      ? buildTagTierGraph(companies, centerLabel, centerType, expandedTiers, tagTierMode, tagTierContext)
      : buildGraph(companies, centerLabel, centerType, expandedTiers)
  ), [companies, centerLabel, centerType, expandedTiers, useTagTiers, tagTierMode, tagTierContext]);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(entries => { const r = entries[0].contentRect; setDims({ w: Math.max(360, r.width), h: Math.max(400, r.height) }); });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!isFullView) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setIsFullView(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isFullView]);

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.d3Force('charge')?.strength(-280);
    fg.d3Force('link')?.distance(l => l.kind === 'spine' ? 140 : 65);
    fg.d3Force('center')?.strength(0.05);
    setTimeout(() => fg.zoomToFit(600, 60), 500);
  }, [graph]);

  const matchSet = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.trim().toLowerCase();
    const m = new Set();
    graph.nodes.forEach(n => { if ([n.label, n.data?.organization].filter(Boolean).join(' ').toLowerCase().includes(q)) m.add(n.id); });
    return m;
  }, [search, graph.nodes]);

  const neighborSet = useMemo(() => {
    const focal = hoverNode || activeNode;
    if (!focal) return null;
    const ids = new Set([focal.id]);
    graph.links.forEach(l => {
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      if (s === focal.id) ids.add(t);
      if (t === focal.id) ids.add(s);
    });
    return ids;
  }, [hoverNode, activeNode, graph.links]);

  const isNodeFaded = useCallback(n => {
    if (matchSet && !matchSet.has(n.id)) return true;
    if (neighborSet && !neighborSet.has(n.id)) return true;
    return false;
  }, [matchSet, neighborSet]);

  const paintNode = useCallback((node, ctx, globalScale) => {
    const faded = isNodeFaded(node);
    ctx.globalAlpha = faded ? 0.12 : 1;
    const r = node.size || 6;
    const isFocal = (hoverNode?.id === node.id) || (activeNode?.id === node.id);

    if ((isFocal || (matchSet && matchSet.has(node.id))) && !faded) {
      ctx.beginPath(); ctx.arc(node.x, node.y, r + 8, 0, 2 * Math.PI);
      ctx.fillStyle = (node.color || '#888') + '22'; ctx.fill();
    }

    ctx.beginPath(); ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    if (node.kind === 'center') {
      ctx.fillStyle = isDark ? '#1A1A19' : '#0f172a'; ctx.fill();
      ctx.lineWidth = 3; ctx.strokeStyle = node.color; ctx.stroke();
    } else if (node.kind === 'tier') {
      ctx.fillStyle = isDark ? node.color + '30' : node.color + '20'; ctx.fill();
      ctx.lineWidth = 2; ctx.strokeStyle = node.color; ctx.stroke();
      if (node.isExpanded) {
        ctx.fillStyle = node.color; ctx.font = `bold ${r * 0.8}px "Inter",sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('−', node.x, node.y);
      } else {
        ctx.fillStyle = node.color; ctx.font = `bold ${r * 0.8}px "Inter",sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('+', node.x, node.y);
      }
    } else {
      ctx.fillStyle = node.color; ctx.fill();
      ctx.lineWidth = 1.2; ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.15)' : '#fff'; ctx.stroke();
    }
    ctx.globalAlpha = 1;

    const showLabel = !faded && (
      node.kind === 'center'
      || node.kind === 'tier'
      || node.kind === 'company'
      || globalScale > 1.5
      || isFocal
    );
    if (showLabel) {
      const label = node.label || '';
      const maxLen = node.kind === 'tier' ? 35 : 22;
      const truncated = label.length > maxLen ? label.substring(0, maxLen - 1) + '…' : label;
      const fontSize = node.kind === 'center' ? 13 / Math.max(0.8, globalScale) + 3 : node.kind === 'tier' ? 9 / Math.max(0.8, globalScale) + 2 : 8 / Math.max(0.9, globalScale) + 2;
      ctx.font = `${node.kind !== 'company' ? 700 : 400} ${fontSize}px "Inter","Manrope",sans-serif`;
      ctx.fillStyle = isDark ? 'rgba(255,255,255,0.85)' : '#1e293b';
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(truncated, node.x, node.y + r + 4);
      if (node.kind === 'tier') {
        ctx.font = `600 ${fontSize * 0.85}px "JetBrains Mono",monospace`;
        ctx.fillStyle = node.color;
        ctx.fillText(`${node.companyCount} companies`, node.x, node.y + r + 4 + fontSize + 2);
      }
    }
  }, [isDark, hoverNode, activeNode, matchSet, isNodeFaded]);

  const linkColor = useCallback(l => {
    const sId = typeof l.source === 'object' ? l.source.id : l.source;
    const tId = typeof l.target === 'object' ? l.target.id : l.target;
    const focal = hoverNode || activeNode;
    const sNode = graph.nodes.find(n => n.id === sId);
    const tNode = graph.nodes.find(n => n.id === tId);
    if ((sNode && isNodeFaded(sNode)) || (tNode && isNodeFaded(tNode))) return isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)';
    if (focal && (sId === focal.id || tId === focal.id)) return l.color || (isDark ? '#fff' : '#1e293b');
    return isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  }, [hoverNode, activeNode, graph.nodes, isDark, isNodeFaded]);

  const linkWidth = useCallback(l => {
    const sId = typeof l.source === 'object' ? l.source.id : l.source;
    const tId = typeof l.target === 'object' ? l.target.id : l.target;
    const focal = hoverNode || activeNode;
    if (focal && (sId === focal.id || tId === focal.id)) return 2;
    return l.kind === 'spine' ? 1.6 : 0.8;
  }, [hoverNode, activeNode]);

  const handleNodeHover = useCallback((node) => {
    if (!node && isTooltipHovered) return;
    setHoverNode(node || null);
    if (node && wrapRef.current) {
      const coords = fgRef.current?.graph2ScreenCoords?.(node.x, node.y);
      if (coords) {
        const rect = wrapRef.current.getBoundingClientRect();
        setTooltipPos({ x: Math.min(coords.x, rect.width - 300), y: Math.min(coords.y, rect.height - 200) });
      }
    }
  }, [isTooltipHovered]);

  const handleNodeClick = useCallback(node => {
    if (node.kind === 'tier') {
      setExpandedTiers(prev => ({ ...prev, [node.tierKey]: !prev[node.tierKey] }));
    } else if (node.kind === 'company' && node.data) {
      if (wrapRef.current) {
        const coords = fgRef.current?.graph2ScreenCoords?.(node.x, node.y);
        if (coords) {
          const rect = wrapRef.current.getBoundingClientRect();
          setTooltipPos({ x: Math.min(coords.x, rect.width - 300), y: Math.min(coords.y, rect.height - 200) });
        }
      }
      setActiveNode(prev => prev?.id === node.id ? null : node);
    } else if (node.kind === 'center') {
      // Toggle all tiers
      const tiers = graph.tiers || [];
      setExpandedTiers(prev => {
        const anyOpen = Object.values(prev).some(Boolean);
        const next = {};
        tiers.forEach(t => { next[t.key] = !anyOpen; });
        return next;
      });
    }
  }, [graph.tiers]);

  const handleBgClick = useCallback(() => {
    setActiveNode(null);
    setHoverNode(null);
    setIsTooltipHovered(false);
  }, []);

  const tooltipNode = useMemo(() => {
    if (hoverNode?.kind === 'company') return hoverNode;
    if (activeNode?.kind === 'company') return activeNode;
    return null;
  }, [hoverNode, activeNode]);

  const handleFindPeople = useCallback((company) => {
    if (isFullView) setIsFullView(false);
    setActiveNode(null);
    setHoverNode(null);
    setIsTooltipHovered(false);
    onCompanyClick?.(company, 'people');
  }, [isFullView, onCompanyClick]);

  const handleFindRoles = useCallback((company) => {
    if (isFullView) setIsFullView(false);
    setActiveNode(null);
    setHoverNode(null);
    setIsTooltipHovered(false);
    onCompanyClick?.(company, 'roles');
  }, [isFullView, onCompanyClick]);

  const bgColor = isDark ? 'rgba(13,16,25,0.6)' : 'rgba(245,248,255,0.8)';
  const dotPattern = isDark
    ? 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.10) 1.1px, transparent 0)'
    : 'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.12) 1.1px, transparent 0)';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const panelBg = isDark ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.95)';
  const tiers = graph.tiers || [];
  const tierHelp = graph.tierHelp || {};
  const tierGroups = graph.tierGroups || {};

  if (!companies || companies.length === 0) {
    return <div style={{ padding: '40px 20px', textAlign: 'center', color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8', fontSize: 13 }}>No companies to visualize.</div>;
  }

  const totalContacts = companies.reduce((s, c) => s + (c.count || 0), 0);

  return (
    <div style={{
      position: isFullView ? 'fixed' : 'relative',
      inset: isFullView ? 16 : 'auto',
      zIndex: isFullView ? 1200 : 'auto',
      borderRadius: isFullView ? 18 : 16,
      overflow: 'hidden',
      border: `1px solid ${borderColor}`,
      background: bgColor,
      boxShadow: isFullView ? (isDark ? '0 24px 80px rgba(0,0,0,0.55)' : '0 20px 60px rgba(15,23,42,0.2)') : 'none',
      ...style
    }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: dotPattern,
          backgroundSize: '26px 26px',
          opacity: isDark ? 0.99 : 0.60,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div ref={wrapRef} style={{ width: '100%', height: isFullView ? 'calc(100vh - 32px)' : 520, position: 'relative', zIndex: 1 }}>
        <ForceGraph2D ref={fgRef} graphData={graph} width={dims.w} height={dims.h} backgroundColor="rgba(0,0,0,0)"
          nodeRelSize={4} nodeCanvasObject={paintNode} nodeCanvasObjectMode={() => 'replace'}
          nodePointerAreaPaint={(node, color, ctx) => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(node.x, node.y, (node.size || 6) + 5, 0, 2 * Math.PI); ctx.fill(); }}
          linkColor={linkColor} linkWidth={linkWidth}
          linkDirectionalParticles={l => { const focal = hoverNode || activeNode; if (!focal) return 0; const sId = typeof l.source === 'object' ? l.source.id : l.source; const tId = typeof l.target === 'object' ? l.target.id : l.target; return (sId === focal.id || tId === focal.id) ? 2 : 0; }}
          linkDirectionalParticleWidth={2} linkDirectionalParticleColor={l => l.color || '#888'}
          cooldownTicks={200} onNodeHover={handleNodeHover} onNodeClick={handleNodeClick} onBackgroundClick={handleBgClick}
          enableNodeDrag={true} minZoom={0.3} maxZoom={6}
        />

        {/* Search */}
        <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 10, width: 220, background: panelBg, border: `1px solid ${borderColor}`, backdropFilter: 'blur(12px)', boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.06)' }}>
          <Search size={13} color={isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8'} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies…" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: isDark ? '#fff' : '#1e293b', width: '100%', fontFamily: "'Inter',sans-serif" }} />
          {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}><X size={12} color={isDark ? 'rgba(255,255,255,0.5)' : '#94a3b8'} /></button>}
        </div>

        {/* Toolbar */}
        <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', borderRadius: 10, overflow: 'hidden', background: panelBg, border: `1px solid ${borderColor}`, backdropFilter: 'blur(12px)' }}>
          {[
            { icon: <ZoomIn size={14} />, action: () => fgRef.current?.zoom(fgRef.current.zoom() * 1.4, 300), title: 'Zoom in' },
            { icon: <ZoomOut size={14} />, action: () => fgRef.current?.zoom(fgRef.current.zoom() / 1.4, 300), title: 'Zoom out' },
            { icon: <RotateCcw size={14} />, action: () => fgRef.current?.zoomToFit(500, 60), title: 'Reset' },
            { icon: isFullView ? <Minimize2 size={14} /> : <Maximize2 size={14} />, action: () => setIsFullView(v => !v), title: isFullView ? 'Exit full view' : 'Full view' },
          ].map((btn, i) => (
            <button key={i} onClick={btn.action} title={btn.title} style={{ padding: '8px 10px', background: 'none', border: 'none', cursor: 'pointer', color: isDark ? 'rgba(255,255,255,0.6)' : '#64748b', borderRight: i < 3 ? `1px solid ${borderColor}` : 'none', display: 'flex', alignItems: 'center' }}>{btn.icon}</button>
          ))}
        </div>

        {/* Tier Legend */}
        <div style={{ position: 'absolute', bottom: 12, left: 12, padding: '10px 14px', borderRadius: 12, background: panelBg, border: `1px solid ${borderColor}`, backdropFilter: 'blur(12px)', maxWidth: 280 }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', color: isDark ? 'rgba(255,255,255,0.35)' : '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Tiers <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— click to toggle</span></div>
          {tiers.map(t => {
            const count = tierGroups[t.key] || 0;
            if (count === 0) return null;
            const isExp = expandedTiers[t.key];
            return (
              <button key={t.key} onClick={() => setExpandedTiers(prev => ({ ...prev, [t.key]: !prev[t.key] }))}
                title={tierHelp[t.key] || ''}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, flexShrink: 0, boxShadow: isExp ? `0 0 8px ${t.color}` : 'none' }} />
                <span style={{ fontSize: 10, fontWeight: 600, color: isExp ? (isDark ? '#fff' : '#0f172a') : (isDark ? 'rgba(255,255,255,0.45)' : '#64748b'), flex: 1, lineHeight: 1.3 }}>{t.label}</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.color, fontFamily: "'JetBrains Mono',monospace" }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Stats */}
        <div style={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 8, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
          <span style={{ padding: '4px 10px', borderRadius: 8, background: panelBg, border: `1px solid ${borderColor}` }}>{companies.length} companies</span>
          <span style={{ padding: '4px 10px', borderRadius: 8, background: panelBg, border: `1px solid ${borderColor}` }}>{totalContacts} contacts</span>
        </div>

        {/* Tooltip */}
        <AnimatePresence>
          {tooltipNode && (
            <NodeTooltip key={tooltipNode.id} node={tooltipNode} isDark={isDark} position={tooltipPos}
              onMouseEnter={() => setIsTooltipHovered(true)}
              onMouseLeave={() => {
                setIsTooltipHovered(false);
                if (!activeNode) setHoverNode(null);
              }}
              tagTierContext={tagTierContext}
              onFindPeople={handleFindPeople}
              onFindRoles={handleFindRoles} />
          )}
          {hoverNode?.kind === 'tier' && (
            <TierTooltip
              key={hoverNode.id}
              node={hoverNode}
              isDark={isDark}
              position={tooltipPos}
              description={tierHelp[hoverNode?.tierKey]}
            />
          )}
        </AnimatePresence>

        {/* Tip */}
        <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', fontSize: 10, color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          Click center to expand all · click a tier to show companies · hover for details
        </div>
      </div>
    </div>
  );
};

export default NetworkMapView;
